import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  LeaseAgreementVM,
  LeaseAgreementService,
  EntityType,
  PeriodEnum,
} from "@propertelligent/client-api";
import PastLeaseCardItem from "../../../PastLease/PastLeaseCardItem";
import apiHelper from "../../../../utils/apiHelper";
import SimpleSpinner from "../../../../components/loader";
import EmptyState from "../../../emptystate";
import H2 from "../../../typography/H2";
import { GetImages } from "../../../../utils/GetImages";
import { usePermissions } from "../../../../hooks/usePermissions";

interface PastLeaseTabContentProps {
  id?: number;
  tenatClassName?: string;
  entityType?: EntityType;
}

const PastLeaseTabContent: React.FC<PastLeaseTabContentProps> = ({
  id: propId,
  tenatClassName,
  entityType = EntityType.Tenant,
}) => {
  const { id: paramId } = useParams();
  const entityId = paramId === undefined ? Number(propId) : Number(paramId);

  const [leases, setLeases] = useState<LeaseAgreementVM[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { canWrite } = usePermissions();
  const hasWriteAccess = canWrite("Leases");

  useEffect(() => {
    if (!entityId) return;

    const fetchPastLeases = async () => {
      try {
        setIsLoading(true);
       
        let response;
        if (entityType === EntityType.Tenant) {
          response = await apiHelper(() => LeaseAgreementService.getApiLeaseAgreementTenantlist(entityId, undefined, undefined, undefined, undefined, undefined, undefined), { showNotification: false });
        } else if (entityType === EntityType.Property) {
          response = await apiHelper(() => LeaseAgreementService.getApiLeaseAgreementPropertyId(entityId, undefined, undefined, undefined, undefined, undefined, undefined), { showNotification: false });
        } else if (entityType === EntityType.Unit) {
          response = await apiHelper(() => LeaseAgreementService.getApiLeaseAgreementUnitlist(entityId, undefined, undefined, undefined, undefined, undefined, undefined), { showNotification: false });
        }

        if (response?.data?.success) {
          setLeases(response.data.success);
        } else {
          setLeases([]);
        }
      } catch (err) {
        setError("Failed to fetch past leases");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPastLeases();
  }, [entityId, entityType]);

  // Grouping and Sorting Logic
  const categorizedLeases = useMemo(() => {
    const sorted = [...leases].sort((a, b) => Number(b.leasePeriod) - Number(a.leasePeriod));
    return {
      future: sorted.filter(l => l.leasePeriod === PeriodEnum.Future),
      current: sorted.filter(l => l.leasePeriod === PeriodEnum.Current),
      past: sorted.filter(l => l.leasePeriod === PeriodEnum.Past),
    };
  }, [leases]);

  if (isLoading) return <SimpleSpinner myClassName={"Icon50"} isFullPage={true} />;
  if (error) return <div>{error}</div>;


  

  const renderLeaseSection = (title: string, leaseList: LeaseAgreementVM[]) => (
    leaseList.length > 0 && (
      <div className="lease-category-section mb-4">
        <div className="top-listing-header-top tenant-additional-top-listing-header-top">
          <H2 text={title} />
        </div>
        {leaseList.map((lease) => (
          <PastLeaseCardItem
            key={lease.id}
            listingData={lease}
            className="no-side-margin"
            isNotLink={true}
            hasWriteAccess={hasWriteAccess}
          />
        ))}
      </div>
    )
  );

  return (
    <div className={`${tenatClassName ? "" : "past-leases-container content-wrapper container-fluid"}`}>
      {leases.length > 0 ? (
        <>
          {renderLeaseSection("Future Lease(s)", categorizedLeases.future)}
          {renderLeaseSection("Current Lease(s)", categorizedLeases.current)}
          {renderLeaseSection("Past Lease(s)", categorizedLeases.past)}
        </>
      ) : (
        <EmptyState
          icon={GetImages.emptyStateLease}
          heading="No Leases Found"
          isButton={false}
        />
      )}
    </div>
  );
};

export default PastLeaseTabContent;